import { Auth } from 'aws-amplify';
import { useCallback, useContext, useEffect, useRef } from 'react';

import { PortalConfigContext } from 'src/context/portalConfigContext';
import { usePostAuditEventMutation } from 'src/services/api/auditLogApi';
import { sendAuditEventOnUnload } from 'src/utils/auditEventBeacon';

// --- Constants ---
const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const THROTTLE_MS = 1000; // 1 second
const LEADER_HEARTBEAT_MS = 5000; // 5 seconds
const LEADER_STALE_MS = 10000; // 10 seconds
const DEBOUNCE_MS = 5000; // 5 seconds between events
const TOKEN_RETRY_MS = 2000; // 2 seconds retry for token caching
const TOKEN_REFRESH_MS = 5 * 60 * 1000; // 5 minutes token refresh

const LEADER_KEY = 'assembly_session_leader';
const SESSION_STATE_KEY_PREFIX = 'assembly_session_state_';

const ACTIVITY_EVENTS: Array<[string, AddEventListenerOptions?]> = [
  ['mousedown'],
  ['keydown'],
  ['scroll', { passive: true }],
  ['touchstart'],
];

// --- Types ---
type SessionState = 'idle' | 'active';

interface PersistedState {
  state: SessionState;
  lastActivityAt: number;
  sessionStartedAt: number;
}

interface LeaderEntry {
  tabId: string;
  timestamp: number;
}

// --- Hook ---
export const useActivitySessionTracker = () => {
  const portalConfig = useContext(PortalConfigContext);
  const [postAuditEvent] = usePostAuditEventMutation();

  // Refs for mutable state accessible in synchronous handlers
  const stateRef = useRef<SessionState>('idle');
  const lastActivityRef = useRef<number>(0);
  const sessionStartedAtRef = useRef<number>(0);
  const lastEventEmittedAtRef = useRef<number>(0);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaderIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tabIdRef = useRef<string>(
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Math.random().toString(36).slice(2)}${Date.now()}`,
  );
  const cachedTokenRef = useRef<string | null>(null);
  const cachedPortalHeaderRef = useRef<string>('');
  const workspaceRef = useRef<{ workspaceId: string; workspaceName: string }>({
    workspaceId: '',
    workspaceName: '',
  });
  const lastThrottledActivityRef = useRef<number>(0);
  const mountedRef = useRef(true);

  // Stable reference to postAuditEvent
  const postAuditEventRef = useRef(postAuditEvent);
  postAuditEventRef.current = postAuditEvent;

  const portalId = portalConfig.portalHeader;
  const sessionStateKey = `${SESSION_STATE_KEY_PREFIX}${portalId}`;

  // --- Helpers ---
  const persistState = useCallback(
    (state: SessionState, lastActivityAt: number, sessionStartedAt: number) => {
      try {
        const data: PersistedState = {
          state,
          lastActivityAt,
          sessionStartedAt,
        };
        sessionStorage.setItem(sessionStateKey, JSON.stringify(data));
      } catch {
        // sessionStorage may be unavailable
      }
    },
    [sessionStateKey],
  );

  const loadPersistedState = useCallback((): PersistedState | null => {
    try {
      const raw = sessionStorage.getItem(sessionStateKey);
      if (!raw) return null;
      return JSON.parse(raw) as PersistedState;
    } catch {
      return null;
    }
  }, [sessionStateKey]);

  const isLeader = useCallback((): boolean => {
    try {
      const raw = localStorage.getItem(LEADER_KEY);
      if (!raw) return false;
      const entry: LeaderEntry = JSON.parse(raw);
      return entry.tabId === tabIdRef.current;
    } catch {
      return false;
    }
  }, []);

  const claimLeadership = useCallback(() => {
    try {
      const entry: LeaderEntry = {
        tabId: tabIdRef.current,
        timestamp: Date.now(),
      };
      localStorage.setItem(LEADER_KEY, JSON.stringify(entry));
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  const releaseLeadership = useCallback(() => {
    if (isLeader()) {
      try {
        localStorage.removeItem(LEADER_KEY);
      } catch {
        // ignore
      }
    }
  }, [isLeader]);

  const canEmitEvent = useCallback((): boolean => {
    const now = Date.now();
    if (now - lastEventEmittedAtRef.current < DEBOUNCE_MS) return false;
    return true;
  }, []);

  const markEventEmitted = useCallback(() => {
    lastEventEmittedAtRef.current = Date.now();
  }, []);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current !== null) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const emitSessionStarted = useCallback(
    (trigger: string) => {
      if (!isLeader() || !canEmitEvent()) return;
      markEventEmitted();
      const now = Date.now();
      stateRef.current = 'active';
      sessionStartedAtRef.current = now;
      lastActivityRef.current = now;
      persistState('active', now, now);
      void postAuditEventRef.current({
        eventType: 'sessionStarted',
        eventDescription: 'Session started',
        context: {
          trigger,
          workspaceId: workspaceRef.current.workspaceId,
          workspaceName: workspaceRef.current.workspaceName,
        },
      });
    },
    [isLeader, canEmitEvent, markEventEmitted, persistState],
  );

  const emitSessionEnded = useCallback(
    (trigger: string) => {
      if (!isLeader() || !canEmitEvent()) return;
      markEventEmitted();
      const sessionDurationMs = Date.now() - sessionStartedAtRef.current;
      stateRef.current = 'idle';
      persistState('idle', Date.now(), 0);
      void postAuditEventRef.current({
        eventType: 'sessionEnded',
        eventDescription: 'Session ended',
        context: {
          trigger,
          sessionDurationMs,
          workspaceId: workspaceRef.current.workspaceId,
          workspaceName: workspaceRef.current.workspaceName,
        },
      });
    },
    [isLeader, canEmitEvent, markEventEmitted, persistState],
  );

  const startInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    inactivityTimerRef.current = setTimeout(() => {
      if (stateRef.current === 'active' && mountedRef.current) {
        emitSessionEnded('inactivityTimeout');
      }
    }, INACTIVITY_TIMEOUT_MS);
  }, [clearInactivityTimer, emitSessionEnded]);

  const handleActivity = useCallback(() => {
    const now = Date.now();

    // Throttle activity processing
    if (now - lastThrottledActivityRef.current < THROTTLE_MS) return;
    lastThrottledActivityRef.current = now;

    lastActivityRef.current = now;

    if (stateRef.current === 'idle') {
      emitSessionStarted('userActivity');
      startInactivityTimer();
    } else {
      // Reset inactivity timer
      persistState('active', now, sessionStartedAtRef.current);
      startInactivityTimer();
    }
  }, [emitSessionStarted, startInactivityTimer, persistState]);

  // --- Cache auth token with retry ---
  // Auth.configure() runs in ConfigureAuth (deeper in the component tree),
  // so Auth.currentSession() may not be available on first mount. We retry
  // every 2 seconds until the token is acquired, then switch to 5-min refresh.
  useEffect(() => {
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let refreshInterval: ReturnType<typeof setInterval> | null = null;
    let stopped = false;

    const cacheToken = async () => {
      try {
        const session = await Auth.currentSession();
        cachedTokenRef.current = session.getIdToken().getJwtToken();
        // Token acquired — switch to periodic refresh
        if (!stopped && !refreshInterval) {
          refreshInterval = setInterval(
            () => void cacheToken(),
            TOKEN_REFRESH_MS,
          );
        }
      } catch {
        // Auth not ready yet — retry shortly
        cachedTokenRef.current = null;
        if (!stopped) {
          retryTimer = setTimeout(() => void cacheToken(), TOKEN_RETRY_MS);
        }
      }
    };

    void cacheToken();

    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [portalId]); // Re-cache when workspace changes

  // --- Cache workspace info and portal header in refs ---
  useEffect(() => {
    workspaceRef.current = {
      workspaceId: portalConfig.portalHeader,
      workspaceName: portalConfig.name,
    };
    cachedPortalHeaderRef.current = portalConfig.portalHeader;
  }, [portalConfig.portalHeader, portalConfig.name]);

  // --- Main effect: leader election, activity listeners, visibility, beforeunload ---
  useEffect(() => {
    mountedRef.current = true;

    // Claim leadership if no current leader or leader is stale
    const tryClaimLeadership = () => {
      try {
        const raw = localStorage.getItem(LEADER_KEY);
        if (!raw) {
          claimLeadership();
          return;
        }
        const entry: LeaderEntry = JSON.parse(raw);
        if (Date.now() - entry.timestamp > LEADER_STALE_MS) {
          claimLeadership();
        }
      } catch {
        claimLeadership();
      }
    };

    tryClaimLeadership();

    // Leader heartbeat
    leaderIntervalRef.current = setInterval(() => {
      if (isLeader()) {
        claimLeadership(); // refresh timestamp
      } else {
        tryClaimLeadership(); // try to claim if stale
      }
    }, LEADER_HEARTBEAT_MS);

    // Storage event listener for leader changes
    const handleStorage = (e: StorageEvent) => {
      if (e.key === LEADER_KEY) {
        tryClaimLeadership();
      }
    };
    window.addEventListener('storage', handleStorage);

    // Resume persisted state or start fresh
    const persisted = loadPersistedState();
    if (
      persisted &&
      persisted.state === 'active' &&
      Date.now() - persisted.lastActivityAt < INACTIVITY_TIMEOUT_MS
    ) {
      // Resume active state without re-emitting sessionStarted
      stateRef.current = 'active';
      lastActivityRef.current = persisted.lastActivityAt;
      sessionStartedAtRef.current = persisted.sessionStartedAt;
      // Set inactivity timer for remaining time
      const remaining =
        INACTIVITY_TIMEOUT_MS - (Date.now() - persisted.lastActivityAt);
      clearInactivityTimer();
      inactivityTimerRef.current = setTimeout(() => {
        if (stateRef.current === 'active' && mountedRef.current) {
          emitSessionEnded('inactivityTimeout');
        }
      }, remaining);
    }
    // No eager sessionStarted on page load — Auth.configure() runs after
    // this effect (ConfigureAuth is deeper in the component tree), so RTK
    // mutations that call Auth.currentSession() would fail silently.
    // Instead, the first user activity (mousedown/keydown/scroll/touch)
    // triggers idle→active → emitSessionStarted('userActivity'), which
    // fires after Auth is configured.

    // Activity listeners
    const activityHandler = () => handleActivity();
    ACTIVITY_EVENTS.forEach(([event, options]) => {
      document.addEventListener(event, activityHandler, options);
    });

    // Visibility change handler
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Tab went hidden — the inactivity timer will still fire
        // Nothing extra needed; timer handles expiration
      } else if (document.visibilityState === 'visible') {
        // Tab became visible again
        if (stateRef.current === 'active') {
          const elapsed = Date.now() - lastActivityRef.current;
          if (elapsed > INACTIVITY_TIMEOUT_MS) {
            // Session should have ended while hidden — emit sessionEnded.
            // Don't auto-start a new session here; let the next real user
            // activity (mousedown/keydown) trigger sessionStarted. This avoids
            // the debounce blocking the back-to-back emit and prevents starting
            // sessions before Auth is configured on page reload.
            emitSessionEnded('inactivityTimeout');
          } else {
            // Resume timer with remaining time
            const remaining = INACTIVITY_TIMEOUT_MS - elapsed;
            clearInactivityTimer();
            inactivityTimerRef.current = setTimeout(() => {
              if (stateRef.current === 'active' && mountedRef.current) {
                emitSessionEnded('inactivityTimeout');
              }
            }, remaining);
          }
        }
        // If idle and tab becomes visible: do nothing. The next real user
        // activity event will trigger the idle→active transition. This prevents
        // firing sessionStarted before Auth is configured (e.g. on page reload
        // when visibilitychange fires before ConfigureAuth's effect).
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // beforeunload handler — fires on tab close AND workspace switch (page reload).
    // React cleanup does NOT run on page unload, so this is the only chance to
    // send sessionEnded and release leadership for the next page.
    const handleBeforeUnload = () => {
      // Send sessionEnded beacon if active and leader
      if (stateRef.current === 'active' && isLeader()) {
        const token = cachedTokenRef.current;
        const portalHeader = cachedPortalHeaderRef.current;
        if (token && portalHeader) {
          const sessionDurationMs = Date.now() - sessionStartedAtRef.current;
          sendAuditEventOnUnload(
            token,
            portalHeader,
            'sessionEnded',
            'Session ended',
            {
              trigger: 'tabClose',
              sessionDurationMs,
              workspaceId: workspaceRef.current.workspaceId,
              workspaceName: workspaceRef.current.workspaceName,
            },
          );
        }
      }

      // Always release leadership on page unload so the next page (or other
      // tabs) can claim leadership immediately without waiting for staleness.
      releaseLeadership();

      // Update persisted state to idle so reloads don't resume stale session
      persistState('idle', Date.now(), 0);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup (runs on React effect re-run, e.g. portalId change — but NOT on page unload)
    return () => {
      mountedRef.current = false;
      clearInactivityTimer();
      if (leaderIntervalRef.current !== null) {
        clearInterval(leaderIntervalRef.current);
        leaderIntervalRef.current = null;
      }
      ACTIVITY_EVENTS.forEach(([event, options]) => {
        document.removeEventListener(event, activityHandler, options);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('storage', handleStorage);
      releaseLeadership();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalId]);
};
